import React, { useState, useEffect, useRef } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Spinner
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserHistoryLT, fetchUserHistoryLT1, fetchUserHistoryLT3, fetchUserHistoryRO } from "../../slices/users/thunk";
import LuckyTarget1 from "./History-tab/LuckyTarget1";
import LuckyTarget from "./History-tab/LuckyTarget";
import AmericanRoulette from "./History-tab/AmericanRoulette";
import { formatDateTimeDDMMYYYY } from "../../helpers/dateUtils";

const UserHistoryModal = ({ isOpen, toggle, user, gameType }) => {
    const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState('1');
    const [filters, setFilters] = useState({
        page: 1,
        limit: 10,
        type: 'all',
        gameType: gameType || 'lt',
    });
    const isMounted = useRef(true);
    const lastFetchKeyRef = useRef(null);

    // Redux state
    const {
        userHistory,
        historyLoading,
    } = useSelector((state) => ({
        userHistory: state.Users.userHistory,
        historyLoading: state.Users.historyLoading,
    }));

    const userId = user?._id || user?.id;

    // Set gameType based on prop
    const actualGameType = gameType || 'lt';

    // Choose dedicated thunk based on model/gameType
    const isLT1 = actualGameType === "lt1" || actualGameType === "lucky-target-1";
    const isLT3 = actualGameType === "lt3" || actualGameType === "lucky-target-3";
    const isRO = actualGameType === "ro" || actualGameType === "roulette";

    let fetchHistoryThunk = fetchUserHistoryLT;
    if (isLT1) fetchHistoryThunk = fetchUserHistoryLT1;
    if (isLT3) fetchHistoryThunk = fetchUserHistoryLT3;
    if (isRO) fetchHistoryThunk = fetchUserHistoryRO;

    const fetchHistory = (params) => {
        if (!userId) return;

        const keyObj = {
            userId,
            gameType: actualGameType,
            page: params?.page ?? 1,
            limit: params?.limit ?? 10,
            type: params?.type ?? "all",
            order: params?.order,
            orderBy: params?.orderBy,
            sort: params?.sort,
        };
        const key = JSON.stringify(keyObj);
        if (lastFetchKeyRef.current === key) return; // de-dupe identical requests
        lastFetchKeyRef.current = key;

        dispatch(fetchHistoryThunk(userId, { ...params, gameType: actualGameType }));
    };

    // Keep local filters in sync with prop gameType
    useEffect(() => {
        setFilters((prev) => ({ ...prev, gameType: actualGameType }));
    }, [actualGameType]);

    // Get history data
    const historyData = userId ? userHistory[userId] || {} : {};
    const userInfo = historyData.userInfo || {};

    // Reset active tab when modal closes
    useEffect(() => {
        if (!isOpen) {
            setActiveTab('1');
        }
    }, [isOpen]);

    // Fetch history when modal opens or gameType changes
    useEffect(() => {
        if (isOpen && userId && isMounted.current) {
            const resetFilters = {
                page: 1,
                limit: 10,
                type: 'all',
                gameType: actualGameType
            };

            setFilters(resetFilters);
            fetchHistory(resetFilters);
        }
    }, [isOpen, userId, dispatch, actualGameType]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    // Handle page change
    const handlePageChange = (page) => {
        const newFilters = { ...filters, page };
        if (userId) {
            setFilters(newFilters);
            fetchHistory(newFilters);
        }
    };

    // Handle rows per page change
    const handleRowsPerPageChange = (limit) => {
        const newFilters = { ...filters, page: 1, limit };
        if (userId) {
            setFilters(newFilters);
            fetchHistory(newFilters);
        }
    };

    // Used by DataTable's internal pagination/sorting hooks
    const handleFetchData = (newParams = {}) => {
        if (!userId) return;
        const merged = { ...filters, ...newParams, gameType: actualGameType };
        setFilters(merged);
        fetchHistory(merged);
    };

    // Get modal title based on gameType
    const getModalTitle = () => {
        switch (actualGameType) {
            case 'lt':
            case 'lucky-target':
                return "Lucky Target User History";
            case 'lt1':
            case 'lucky-target-1':
                return "Lucky Target 1 User History";
            case 'lt3':
            case 'lucky-target-3':
                return "Lucky Target 2 User History";
            case 'ro':
            case 'roulette':
                return "American Roulette User History";
            default:
                return "User Activity History";
        }
    };

    // Get game name for display
    const getGameName = () => {
        switch (actualGameType) {
            case 'lt':
            case 'lucky-target':
                return "Lucky Target";
            case 'lt1':
            case 'lucky-target-1':
                return "Lucky Target 1";
            case 'lt3':
            case 'lucky-target-3':
                return "Lucky Target 2";
            case 'ro':
            case 'roulette':
                return "American Roulette";
            default:
                return "Game";
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            toggle={toggle}
            size="xl"
            scrollable
            className="modal-fullscreen-lg-down"
        >
            {/* ============= HEADER ============= */}
            <ModalHeader
                toggle={toggle}
                className="bg-white border-0 pb-2"
                style={{
                    boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                    zIndex: 1
                }}
            >
                <div className="d-flex flex-column w-100">
                    <div className="d-flex justify-content-between align-items-center mb-2 gap-2">
                        <h5 className="mb-0 fw-semibold">
                            <i className="ri-user-line me-2 text-primary"></i>
                            {getModalTitle()}
                        </h5>
                        <div className="d-flex gap-2">
                            <span className="badge bg-soft-info text-info border border-info">
                                Operator: {user?.operatorName || 'Admin'}
                            </span>
                            <span className="badge bg-primary">
                                {getGameName()}
                            </span>
                        </div>
                    </div>
                </div>
            </ModalHeader>

            {/* ============= BODY ============= */}
            <ModalBody className="p-0" style={{ backgroundColor: '#f8f9fa', overflowX: 'hidden' }}>
                {historyLoading ? (
                    <div className="text-center py-5">
                        <Spinner color="primary" className="mb-3" style={{ width: '3rem', height: '3rem' }} />
                        <p className="text-muted">Loading {getGameName()} user history...</p>
                    </div>
                ) : !historyData || Object.keys(historyData).length === 0 ? (
                    <div className="text-center py-5">
                        <div className="mb-3">
                            <i className="ri-inbox-line" style={{ fontSize: "60px", color: "#dee2e6" }} />
                        </div>
                        <h5 className="text-muted">No data found</h5>
                        <p className="text-muted">No history data available for this user in {getGameName()}.</p>
                    </div>
                ) : (
                    <>
                        {actualGameType === 'lt' || actualGameType === 'lucky-target' ? (
                            <LuckyTarget
                                historyData={historyData}
                                historyLoading={historyLoading}
                                filters={filters}
                                activeTab={activeTab}
                                setActiveTab={setActiveTab}
                                userId={userId}
                                onFetchData={handleFetchData}
                                handlePageChange={handlePageChange}
                                handleRowsPerPageChange={handleRowsPerPageChange}
                            />
                        ) : actualGameType === 'lt1' || actualGameType === 'lucky-target-1' ? (
                            <LuckyTarget1
                                historyData={historyData}
                                historyLoading={historyLoading}
                                filters={filters}
                                activeTab={activeTab}
                                setActiveTab={setActiveTab}
                                userId={userId}
                                onFetchData={handleFetchData}
                                handlePageChange={handlePageChange}
                                handleRowsPerPageChange={handleRowsPerPageChange}
                            />
                        ) : actualGameType === 'lt3' || actualGameType === 'lucky-target-3' ? (
                            <LuckyTarget1
                                historyData={historyData}
                                historyLoading={historyLoading}
                                filters={filters}
                                activeTab={activeTab}
                                setActiveTab={setActiveTab}
                                userId={userId}
                                onFetchData={handleFetchData}
                                handlePageChange={handlePageChange}
                                handleRowsPerPageChange={handleRowsPerPageChange}
                            />
                        ) : (
                            <AmericanRoulette
                                historyData={historyData}
                                historyLoading={historyLoading}
                                filters={filters}
                                activeTab={activeTab}
                                setActiveTab={setActiveTab}
                                userId={userId}
                                onFetchData={handleFetchData}
                                handlePageChange={handlePageChange}
                                handleRowsPerPageChange={handleRowsPerPageChange}
                            />
                        )}
                    </>
                )}
            </ModalBody>

            {/* ============= FOOTER ============= */}
            <ModalFooter
                className="bg-white border-0"
                style={{
                    boxShadow: '0 -2px 4px rgba(0,0,0,0.08)',
                    zIndex: 1
                }}
            >
                <div className="d-flex justify-content-between w-100 align-items-center">
                    <div className="text-muted">
                        <small>
                            <i className="ri-time-line me-1"></i>
                            Last Updated: {userInfo.lastActivity ? formatDateTimeDDMMYYYY(userInfo.lastActivity) : 'N/A'}
                        </small>
                    </div>
                    <div className="d-flex gap-2">
                        <Button color="light" onClick={toggle} className="px-4">
                            <i className="ri-close-line me-1"></i>
                            Close
                        </Button>
                        <Button
                            color="primary"
                            onClick={() => {
                                if (userId) {
                                    // Reset last fetch key to force refresh
                                    lastFetchKeyRef.current = null;
                                    // Reset to first page and fetch fresh data
                                    const refreshFilters = { ...filters, page: 1 };
                                    setFilters(refreshFilters);
                                    fetchHistory(refreshFilters);
                                }
                            }}
                            disabled={historyLoading}
                            className="px-4"
                        >
                            <i className="ri-refresh-line me-1"></i>
                            Refresh
                        </Button>
                    </div>
                </div>
            </ModalFooter>
        </Modal>
    );
};

export default UserHistoryModal;